import React, { useState, useRef, useCallback, useEffect } from 'react';
import * as XLSX from 'xlsx';
import {
  X,
  Upload,
  FileSpreadsheet,
  AlertCircle,
  CheckCircle,
  AlertTriangle,
  Loader2,
  XCircle,
  SkipForward,
  Ban,
  Download,
  Users,
} from 'lucide-react';
import {
  fetchActiveCampuses,
  fetchManagementPermissions,
  type CampusOption,
  type ManagementPermissions,
} from '@/services/userService';

// ==================== TYPES ====================

interface BulkImportStaffModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type ImportStep = 'upload' | 'preview' | 'importing' | 'results';

// Parsed row from Excel
interface ParsedStaffRow {
  rowNumber: number;
  cnie: string;
  passport: string;
  nom: string;
  prenom: string;
  email: string;
  sexe: string;
  dateNaissance: string;
  nationalite: string;
  fonction: string;
  dateRecrutement: string;
  salaireMensuel: string;
  handicap: string;
  // Fields to be filled by user
  selectedRole: string;
  activiteEnseignement: 'oui' | 'non' | '';
  // Validation
  isValid: boolean;
  validationErrors: string[];
  isDuplicate: boolean;
  duplicateReason: string;
}

// Import result from backend
interface BulkImportStaffResult {
  rowNumber: number;
  success: boolean;
  email?: string;
  nom?: string;
  prenom?: string;
  error?: string;
}

interface BulkImportStaffSkippedDuplicate {
  rowNumber: number;
  email: string;
  nom: string;
  prenom: string;
  reason: string;
}

interface BulkImportStaffRejected {
  rowNumber: number;
  nom: string;
  prenom: string;
  reason: string;
}

interface BulkImportStaffResponse {
  totalRows: number;
  successCount: number;
  errorCount: number;
  skippedCount: number;
  results: BulkImportStaffResult[];
  skippedDuplicates: BulkImportStaffSkippedDuplicate[];
  rejectedMissingRequired: BulkImportStaffRejected[];
}

// Staff roles (excluding TEACHER which has its own bulk import)
const STAFF_ROLE_OPTIONS = [
  { value: 'DIRECTOR_GENERAL', label: 'Directeur Général' },
  { value: 'BRANCH_MANAGER', label: 'Directeur de Campus' },
  { value: 'PEDAGOGICAL_DIRECTOR', label: 'Directeur Pédagogique' },
  { value: 'ACADEMIC_COORDINATOR', label: 'Coordinateur Académique' },
  { value: 'FINANCIAL_MANAGER', label: 'Responsable Financier' },
  { value: 'MARKETING_MANAGER', label: 'Responsable Marketing' },
  { value: 'COMMERCIAL_MANAGER', label: 'Responsable Commercial' },
  { value: 'PROGRAM_HEAD', label: 'Chef de Filière' },
];

// Roles that can also be professors
const STAFF_ROLES_WHO_CAN_TEACH = [
  'DIRECTOR_GENERAL',
  'BRANCH_MANAGER',
  'PEDAGOGICAL_DIRECTOR',
  'ACADEMIC_COORDINATOR',
  'FINANCIAL_MANAGER',
  'MARKETING_MANAGER',
  'COMMERCIAL_MANAGER',
  'PROGRAM_HEAD',
];

// Template headers matching ministry nomenclature for administrative staff
const TEMPLATE_HEADERS = [
  'N° CNIE',
  'N° passeport',
  'Nom',
  'Prénom',
  'Email',
  'Sexe',
  'Date de naissance',
  'Nationalité',
  'Sigle université',
  'Université',
  'Sigle établissement',
  'Etablissement',
  'Fonction',
  'Date de recrutement',
  'Salaire mensuel',
  'Handicapé',
  'Session',
  'Motif de la décision',
];

// Example row for the template
const TEMPLATE_EXAMPLE_ROW = TEMPLATE_HEADERS.map(() => '');

// ==================== HELPER FUNCTIONS ====================

// Normalize string for comparison (remove accents, lowercase)
const normalizeString = (str: string): string => {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
};

// Dynamic column mapping - detects columns by header names
// This allows importing Excel files with different column orders
// Supports Ministry of Education nomenclature format
const HEADER_TO_FIELD_MAP: Record<string, string> = {
  // CNIE variations (Ministry: "N° CNIE")
  'n° cnie': 'cnie',
  'n°cnie': 'cnie',
  'cnie': 'cnie',
  'numero cnie': 'cnie',
  'carte nationale': 'cnie',
  // Passport variations (Ministry: "N° passeport")
  'n° passeport': 'passport',
  'n°passeport': 'passport',
  'passeport': 'passport',
  'numero passeport': 'passport',
  'passport': 'passport',
  // Name variations (Ministry: "Nom")
  'nom': 'nom',
  'nom de famille': 'nom',
  'last name': 'nom',
  // Arabic name (not in ministry template but supported)
  'nom arabe': 'nomArabe',
  // First name variations (Ministry: "Prénom")
  'prenom': 'prenom',
  'first name': 'prenom',
  // Arabic first name (not in ministry template but supported)
  'prenom arabe': 'prenomArabe',
  // Email variations (Ministry: "Email")
  'email': 'email',
  'e-mail': 'email',
  'adresse email': 'email',
  'mail': 'email',
  // Gender variations (Ministry: "Sexe")
  'sexe': 'sexe',
  'genre': 'sexe',
  'gender': 'sexe',
  // Birth date variations (Ministry: "Date de naissance")
  'date de naissance': 'dateNaissance',
  'date naissance': 'dateNaissance',
  'naissance': 'dateNaissance',
  'birth date': 'dateNaissance',
  // Nationality variations (Ministry: "Nationalité")
  'nationalite': 'nationalite',
  'pays': 'nationalite',
  "pays d'origine": 'nationalite',
  'nationality': 'nationalite',
  // Function/Position variations (Ministry: "Fonction")
  'fonction': 'fonction',
  'poste': 'fonction',
  'position': 'fonction',
  'titre': 'fonction',
  // Recruitment date variations (not in ministry template but supported)
  'date de recrutement': 'dateRecrutement',
  'date recrutement': 'dateRecrutement',
  "date d'embauche": 'dateRecrutement',
  'date embauche': 'dateRecrutement',
  'recruitment date': 'dateRecrutement',
  // Salary variations (not in ministry template but supported)
  'salaire': 'salaireMensuel',
  'salaire mensuel': 'salaireMensuel',
  'salaire net': 'salaireMensuel',
  'salary': 'salaireMensuel',
  'remuneration': 'salaireMensuel',
  // Handicap variations (Ministry: "Handicapé")
  'handicape': 'handicap',
  'handicap': 'handicap',
  'pmr': 'handicap',
};


// Fallback fixed column mapping (used if headers can't be detected)
const FIXED_COLUMN_MAPPING: Record<number, string> = {
  0: 'cnie',
  1: 'passport',
  2: 'nom',
  3: 'prenom',
  4: 'email',
  5: 'sexe',
  6: 'dateNaissance',
  7: 'nationalite',
  // 8-11: ignored (university/establishment info)
  12: 'fonction',
  13: 'dateRecrutement',
  14: 'salaireMensuel',
  15: 'handicap',
};

// Detect column mapping from header row
const detectColumnMapping = (headerRow: unknown[]): Record<number, string> => {
  const mapping: Record<number, string> = {};

  headerRow.forEach((cell, index) => {
    if (cell === null || cell === undefined) return;

    const headerText = normalizeString(String(cell));

    // Check if this header matches any known field
    for (const [pattern, fieldName] of Object.entries(HEADER_TO_FIELD_MAP)) {
      if (headerText === pattern || headerText.includes(pattern)) {
        // Don't overwrite if already mapped (first match wins)
        if (!Object.values(mapping).includes(fieldName)) {
          mapping[index] = fieldName;
        }
        break;
      }
    }
  });

  return mapping;
};

// Parse Excel date (Excel serial number or string)
const parseExcelDate = (value: unknown): string => {
  if (!value) return '';

  if (typeof value === 'number') {
    // Excel serial date
    const date = new Date((value - 25569) * 86400 * 1000);
    if (!isNaN(date.getTime())) {
      return date.toISOString().split('T')[0];
    }
  }

  if (typeof value === 'string') {
    // Try to parse DD/MM/YYYY format
    const parts = value.split(/[\/\-\.]/);
    if (parts.length === 3) {
      const [day, month, year] = parts;
      if (day && month && year) {
        const d = parseInt(day, 10);
        const m = parseInt(month, 10);
        const y = parseInt(year, 10);
        if (d > 0 && d <= 31 && m > 0 && m <= 12 && y > 1900) {
          return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        }
      }
    }
    return value;
  }

  return String(value);
};

// Parse gender value
const parseGender = (value: string): string => {
  const normalized = normalizeString(value);
  if (normalized === 'f' || normalized === 'feminin' || normalized === 'female' || normalized === 'féminin') {
    return 'F';
  }
  if (normalized === 'm' || normalized === 'masculin' || normalized === 'male') {
    return 'M';
  }
  return '';
};

// Parse handicap value
const parseHandicap = (value: string): string => {
  const normalized = normalizeString(value);
  if (normalized === 'o' || normalized === 'oui' || normalized === 'yes' || normalized === 'y') {
    return 'XX';
  }
  return 'N';
};

// ==================== MAIN COMPONENT ====================

export const BulkImportStaffModal: React.FC<BulkImportStaffModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [step, setStep] = useState<ImportStep>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<BulkImportStaffResponse | null>(null);

  // Parsed data
  const [parsedRows, setParsedRows] = useState<ParsedStaffRow[]>([]);
  const [validRowsCount, setValidRowsCount] = useState(0);
  const [duplicateRowsCount, setDuplicateRowsCount] = useState(0);

  // Options
  const [campuses, setCampuses] = useState<CampusOption[]>([]);
  const [selectedCampusIds, setSelectedCampusIds] = useState<string[]>([]);
  const [_loadingCampuses, setLoadingCampuses] = useState(false);
  const [_permissions, setPermissions] = useState<ManagementPermissions | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load campuses and permissions
  const loadInitialData = useCallback(async () => {
    setLoadingCampuses(true);
    try {
      const [campusResponse, permissionsResponse] = await Promise.all([
        fetchActiveCampuses(),
        fetchManagementPermissions(),
      ]);
      setCampuses(campusResponse.campuses);
      setPermissions(permissionsResponse);
      // Auto-select if only one campus
      if (campusResponse.campuses.length === 1) {
        setSelectedCampusIds([campusResponse.campuses[0].id]);
      }
    } catch (err) {
      console.error('Failed to load initial data:', err);
    } finally {
      setLoadingCampuses(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadInitialData();
    }
  }, [isOpen, loadInitialData]);

  // Download Excel template
  const handleDownloadTemplate = useCallback(() => {
    // Create CSV content (Excel-compatible)
    const csvContent = [
      TEMPLATE_HEADERS.join(';'),
      TEMPLATE_EXAMPLE_ROW.join(';'),
    ].join('\n');

    // Create blob with BOM for proper Excel encoding
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });

    // Create download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'template_import_personnel.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  // Parse Excel file
  const parseExcelFile = useCallback(async (selectedFile: File): Promise<ParsedStaffRow[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as unknown[][];

          if (jsonData.length < 2) {
            reject(new Error('Le fichier doit contenir au moins une ligne de données'));
            return;
          }

          // Try to detect columns from header row first
          const headerRow = jsonData[0] as unknown[];
          let columnMapping = detectColumnMapping(headerRow);

          // If we detected at least 3 important fields (nom, prenom, email), use dynamic mapping
          // Otherwise fall back to fixed mapping
          const detectedFields = Object.values(columnMapping);
          const hasEssentialFields =
            detectedFields.includes('nom') &&
            detectedFields.includes('prenom') &&
            detectedFields.includes('email');

          if (!hasEssentialFields) {
            console.warn('[BulkImport] Could not detect columns from headers, using fixed mapping');
            columnMapping = FIXED_COLUMN_MAPPING;
          } else {
            console.log('[BulkImport] Detected column mapping:', columnMapping);
          }

          // Track seen values for duplicate detection within file
          const seenEmails = new Set<string>();
          const seenCNIEs = new Set<string>();
          const seenNames = new Set<string>();

          // Parse rows
          const rows: ParsedStaffRow[] = [];
          for (let i = 1; i < jsonData.length; i++) {
            const row = jsonData[i] as unknown[];
            if (!row || row.every(cell => !cell)) continue; // Skip empty rows

            const parsedRow: ParsedStaffRow = {
              rowNumber: i + 1,
              cnie: '',
              passport: '',
              nom: '',
              prenom: '',
              email: '',
              sexe: '',
              dateNaissance: '',
              nationalite: '',
              fonction: '',
              dateRecrutement: '',
              salaireMensuel: '',
              handicap: 'N',
              selectedRole: '',
              activiteEnseignement: '',
              isValid: true,
              validationErrors: [],
              isDuplicate: false,
              duplicateReason: '',
            };

            // Extract values using column mapping
            for (const [colIndex, fieldName] of Object.entries(columnMapping)) {
              const value = row[parseInt(colIndex)];
              if (value !== undefined && value !== null) {
                const strValue = String(value).trim();
                switch (fieldName) {
                  case 'cnie':
                    parsedRow.cnie = strValue.toUpperCase();
                    break;
                  case 'passport':
                    parsedRow.passport = strValue.toUpperCase();
                    break;
                  case 'nom':
                    parsedRow.nom = strValue.toUpperCase();
                    break;
                  case 'prenom':
                    // Capitalize first letter of each word
                    parsedRow.prenom = strValue
                      .split(' ')
                      .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
                      .join(' ');
                    break;
                  case 'email':
                    parsedRow.email = strValue.toLowerCase();
                    break;
                  case 'sexe':
                    parsedRow.sexe = parseGender(strValue);
                    break;
                  case 'dateNaissance':
                    parsedRow.dateNaissance = parseExcelDate(value);
                    break;
                  case 'nationalite':
                    parsedRow.nationalite = strValue.toUpperCase();
                    break;
                  case 'fonction':
                    parsedRow.fonction = strValue;
                    break;
                  case 'dateRecrutement':
                    parsedRow.dateRecrutement = parseExcelDate(value);
                    break;
                  case 'salaireMensuel':
                    // Remove non-numeric characters except decimal point
                    // Also validate it looks like a salary (reasonable number range)
                    const cleanedSalary = strValue.replace(/[^\d.,]/g, '').replace(',', '.');
                    const salaryNum = parseFloat(cleanedSalary);
                    // Only accept if it's a reasonable salary amount (between 1000 and 1000000)
                    if (!isNaN(salaryNum) && salaryNum >= 1000 && salaryNum <= 1000000) {
                      parsedRow.salaireMensuel = cleanedSalary;
                    }
                    break;
                  case 'handicap':
                    parsedRow.handicap = parseHandicap(strValue);
                    break;
                }
              }
            }

            // Validate required fields
            if (!parsedRow.nom) {
              parsedRow.isValid = false;
              parsedRow.validationErrors.push('Nom manquant');
            }
            if (!parsedRow.prenom) {
              parsedRow.isValid = false;
              parsedRow.validationErrors.push('Prénom manquant');
            }
            if (!parsedRow.email) {
              parsedRow.isValid = false;
              parsedRow.validationErrors.push('Email manquant');
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(parsedRow.email)) {
              parsedRow.isValid = false;
              parsedRow.validationErrors.push('Email invalide');
            }

            // Check for duplicates within the file
            if (parsedRow.email && seenEmails.has(parsedRow.email.toLowerCase())) {
              parsedRow.isDuplicate = true;
              parsedRow.duplicateReason = 'Email en double dans le fichier';
            } else if (parsedRow.cnie && seenCNIEs.has(parsedRow.cnie)) {
              parsedRow.isDuplicate = true;
              parsedRow.duplicateReason = 'CNIE en double dans le fichier';
            } else {
              const nameKey = `${parsedRow.nom.toLowerCase()}_${parsedRow.prenom.toLowerCase()}`;
              if (seenNames.has(nameKey)) {
                parsedRow.isDuplicate = true;
                parsedRow.duplicateReason = 'Nom + Prénom en double dans le fichier';
              }
            }

            // Track values for duplicate detection
            if (parsedRow.email) seenEmails.add(parsedRow.email.toLowerCase());
            if (parsedRow.cnie) seenCNIEs.add(parsedRow.cnie);
            if (parsedRow.nom && parsedRow.prenom) {
              seenNames.add(`${parsedRow.nom.toLowerCase()}_${parsedRow.prenom.toLowerCase()}`);
            }

            rows.push(parsedRow);
          }

          resolve(rows);
        } catch (err) {
          reject(new Error('Erreur lors de la lecture du fichier Excel'));
        }
      };

      reader.onerror = () => {
        reject(new Error('Erreur lors de la lecture du fichier'));
      };

      reader.readAsBinaryString(selectedFile);
    });
  }, []);

  const handleFileSelect = async (selectedFile: File) => {
    // Validate file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv',
    ];
    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const hasValidExtension = validExtensions.some(ext =>
      selectedFile.name.toLowerCase().endsWith(ext)
    );

    if (!validTypes.includes(selectedFile.type) && !hasValidExtension) {
      setError('Veuillez sélectionner un fichier Excel (.xlsx, .xls) ou CSV (.csv)');
      return;
    }

    // Validate file size (10MB max)
    if (selectedFile.size > 10 * 1024 * 1024) {
      setError('Le fichier ne doit pas dépasser 10 Mo');
      return;
    }

    setFile(selectedFile);
    setError(null);

    try {
      const rows = await parseExcelFile(selectedFile);
      if (rows.length === 0) {
        setError('Aucune donnée trouvée dans le fichier');
        return;
      }

      setParsedRows(rows);
      setValidRowsCount(rows.filter(r => r.isValid && !r.isDuplicate).length);
      setDuplicateRowsCount(rows.filter(r => r.isDuplicate).length);
      setStep('preview');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la lecture du fichier');
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  // Update row role
  const handleRoleChange = (rowIndex: number, role: string) => {
    setParsedRows(prev => {
      const updated = [...prev];
      updated[rowIndex] = {
        ...updated[rowIndex],
        selectedRole: role,
        // Reset activiteEnseignement if role doesn't support it
        activiteEnseignement: STAFF_ROLES_WHO_CAN_TEACH.includes(role) ? updated[rowIndex].activiteEnseignement : '',
      };
      return updated;
    });
  };

  // Update row salary
  const handleSalaireChange = (rowIndex: number, value: string) => {
    setParsedRows(prev => {
      const updated = [...prev];
      updated[rowIndex] = {
        ...updated[rowIndex],
        salaireMensuel: value,
      };
      return updated;
    });
  };

  // Update row activiteEnseignement
  const handleActiviteEnseignementChange = (rowIndex: number, value: 'oui' | 'non' | '') => {
    setParsedRows(prev => {
      const updated = [...prev];
      updated[rowIndex] = {
        ...updated[rowIndex],
        activiteEnseignement: value,
      };
      return updated;
    });
  };

  // Toggle campus selection
  const handleCampusToggle = (campusId: string) => {
    setSelectedCampusIds(prev => {
      if (prev.includes(campusId)) {
        return prev.filter(id => id !== campusId);
      } else {
        return [...prev, campusId];
      }
    });
  };

  // Check if all non-duplicate rows have roles assigned
  const allRolesAssigned = parsedRows
    .filter(r => !r.isDuplicate)
    .every(r => r.selectedRole);

  // Count rows ready to import (has role and salary, not a duplicate)
  const rowsReadyToImport = parsedRows.filter(r => !r.isDuplicate && r.selectedRole && r.salaireMensuel).length;

  // Check if all non-duplicate rows have salary filled
  const allSalariesFilled = parsedRows
    .filter(r => !r.isDuplicate)
    .every(r => r.salaireMensuel);

  // Check if campus selection is required but not fulfilled
  const campusSelectionRequired = campuses.length > 1 && selectedCampusIds.length === 0;

  const handleImport = async () => {
    if (!file || rowsReadyToImport === 0) return;

    setStep('importing');
    setError(null);

    try {
      // Prepare data for backend - send all rows with roles selected (backend will validate)
      const importData = parsedRows
        .filter(r => !r.isDuplicate && r.selectedRole)
        .map(r => ({
          rowNumber: r.rowNumber,
          cnie: r.cnie,
          passport: r.passport,
          nom: r.nom,
          prenom: r.prenom,
          email: r.email,
          sexe: r.sexe,
          dateNaissance: r.dateNaissance,
          nationalite: r.nationalite,
          fonction: r.fonction,
          dateRecrutement: r.dateRecrutement,
          salaireMensuel: r.salaireMensuel,
          handicap: r.handicap,
          role: r.selectedRole,
          isAlsoProfessor: r.activiteEnseignement === 'oui',
        }));

      // Call backend API - DO NOT send campusIds if empty (causes validation error)
      const options: { campusIds?: string[]; academicYearId?: string } = {};
      if (selectedCampusIds.length > 0) {
        options.campusIds = selectedCampusIds;
      }
      const response = await bulkImportStaff(importData, options);

      setResults(response);
      setStep('results');
    } catch (err) {
      console.error('[BulkImportStaff] Error:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors de l\'import');
      setStep('preview');
    }
  };

  const handleClose = () => {
    const hadSuccessfulImports = results && results.successCount > 0;

    // Reset state
    setStep('upload');
    setFile(null);
    setError(null);
    setResults(null);
    setParsedRows([]);
    setSelectedCampusIds([]);

    if (hadSuccessfulImports) {
      onSuccess();
    }
    onClose();
  };

  const handleStartOver = () => {
    setStep('upload');
    setFile(null);
    setError(null);
    setResults(null);
    setParsedRows([]);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className={`relative bg-white rounded-xl shadow-xl w-full p-6 z-10 ${
          step === 'preview' ? 'max-w-6xl' : 'max-w-2xl'
        }`}>
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          >
            <X size={20} />
          </button>

          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-blue-100 rounded-full">
              <Users size={24} className="text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Import en masse du personnel
              </h2>
              <p className="text-sm text-gray-500">
                Importez plusieurs membres du personnel depuis un fichier Excel
              </p>
            </div>
          </div>

          {/* Step: Upload */}
          {step === 'upload' && (
            <div>
              {/* Drop zone */}
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                  dragActive
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                }`}
              >
                <Upload
                  size={48}
                  className={`mx-auto mb-4 ${
                    dragActive ? 'text-blue-500' : 'text-gray-400'
                  }`}
                />
                <p className="text-lg font-medium text-gray-700 mb-2">
                  Glissez-déposez votre fichier Excel ici
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  ou cliquez pour sélectionner un fichier
                </p>
                <p className="text-xs text-gray-400">
                  Formats acceptés : .xlsx, .xls, .csv (max 10 Mo)
                </p>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
                onChange={handleFileInputChange}
                className="hidden"
              />

              {/* Error message */}
              {error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-800">
                  <AlertCircle size={18} />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              {/* Help section */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-900">Format attendu</h4>
                  <button
                    onClick={handleDownloadTemplate}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                  >
                    <Download size={16} />
                    Télécharger le modèle
                  </button>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  Le fichier Excel doit contenir les colonnes suivantes :
                </p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li><span className="font-medium text-red-600">* Nom</span> - Nom de famille (obligatoire)</li>
                  <li><span className="font-medium text-red-600">* Prénom</span> - Prénom (obligatoire)</li>
                  <li><span className="font-medium text-red-600">* Email</span> - Adresse email (obligatoire)</li>
                  <li><span className="font-medium">N° CNIE</span> - Carte nationale d'identité</li>
                  <li><span className="font-medium">Fonction</span> - Poste occupé</li>
                  <li><span className="font-medium">Date de recrutement</span> - Format DD/MM/YYYY</li>
                  <li><span className="font-medium">Salaire mensuel</span> - Montant en DH</li>
                  <li><span className="font-medium">Sexe</span> - m ou f</li>
                  <li><span className="font-medium">Date de naissance</span> - Format DD/MM/YYYY</li>
                  <li className="text-gray-400">+ autres champs optionnels...</li>
                </ul>
                <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-blue-800">
                    <strong>Note :</strong> Après l'upload, vous devrez attribuer un rôle à chaque membre du personnel
                    et indiquer leur activité d'enseignement (Oui/Non).
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Step: Preview */}
          {step === 'preview' && (
            <div>
              {/* File info */}
              <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg mb-4">
                <FileSpreadsheet size={24} className="text-green-600" />
                <div className="flex-1">
                  <p className="font-medium text-green-900">{file?.name}</p>
                  <p className="text-sm text-green-700">
                    {parsedRows.length} ligne(s) trouvée(s) • {validRowsCount} valide(s) • {duplicateRowsCount} doublon(s)
                  </p>
                </div>
                <button
                  onClick={handleStartOver}
                  className="text-green-600 hover:text-green-800"
                >
                  Changer
                </button>
              </div>

              {/* Campus selection - Required for multi-campus institutions */}
              {campuses.length > 1 && (
                <div className={`mb-4 p-4 rounded-lg ${
                  selectedCampusIds.length === 0 ? 'bg-red-50 border border-red-200' : 'bg-gray-50'
                }`}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Campus assigné(s) au personnel <span className="text-red-500">*</span>
                  </label>
                  {selectedCampusIds.length === 0 && (
                    <p className="text-sm text-red-600 mb-2">
                      Veuillez sélectionner au moins un campus pour l'import
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {campuses.map((campus) => (
                      <label
                        key={campus.id}
                        className={`flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer transition-colors ${
                          selectedCampusIds.includes(campus.id)
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:bg-gray-100'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedCampusIds.includes(campus.id)}
                          onChange={() => handleCampusToggle(campus.id)}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-900">
                          {campus.name}
                          {campus.isMainCampus && (
                            <span className="ml-1 text-xs text-blue-600">(Principal)</span>
                          )}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Preview table */}
              <div className="border border-gray-200 rounded-lg overflow-hidden mb-4">
                <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">#</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Nom</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Prénom</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Email</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">CNIE</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Passeport</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Fonction</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700 min-w-[120px]">
                          Salaire (DH) <span className="text-red-500">*</span>
                        </th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700 min-w-[180px]">
                          Rôle <span className="text-red-500">*</span>
                        </th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700 min-w-[160px]">
                          Activité d'enseignement
                        </th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Validation</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedRows.map((row, index) => (
                        <tr
                          key={index}
                          className={`border-t ${
                            row.isDuplicate
                              ? 'bg-yellow-50'
                              : !row.isValid
                              ? 'bg-red-50'
                              : index % 2 === 0
                              ? 'bg-white'
                              : 'bg-gray-50'
                          }`}
                        >
                          <td className="px-3 py-2 text-gray-500">{row.rowNumber}</td>
                          <td className="px-3 py-2 font-medium">{row.nom}</td>
                          <td className="px-3 py-2">{row.prenom}</td>
                          <td className="px-3 py-2 text-gray-600">{row.email}</td>
                          <td className="px-3 py-2 text-gray-600">{row.cnie || '-'}</td>
                          <td className="px-3 py-2 text-gray-600">{row.passport || '-'}</td>
                          <td className="px-3 py-2 text-gray-600">{row.fonction || '-'}</td>
                          <td className="px-3 py-2">
                            {!row.isDuplicate ? (
                              <input
                                type="number"
                                min="0"
                                step="100"
                                value={row.salaireMensuel || ''}
                                onChange={(e) => handleSalaireChange(index, e.target.value)}
                                placeholder="Ex: 8000"
                                className={`w-full px-2 py-1 border rounded text-sm ${
                                  !row.salaireMensuel ? 'border-red-300' : 'border-gray-300'
                                }`}
                              />
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            {!row.isDuplicate ? (
                              <select
                                value={row.selectedRole}
                                onChange={(e) => handleRoleChange(index, e.target.value)}
                                className={`w-full px-2 py-1 border rounded text-sm ${
                                  !row.selectedRole ? 'border-red-300' : 'border-gray-300'
                                }`}
                              >
                                <option value="">Sélectionner...</option>
                                {STAFF_ROLE_OPTIONS.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            {!row.isDuplicate ? (
                              <select
                                value={row.activiteEnseignement}
                                onChange={(e) => handleActiviteEnseignementChange(index, e.target.value as 'oui' | 'non' | '')}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                              >
                                <option value="">-</option>
                                <option value="oui">Oui</option>
                                <option value="non">Non</option>
                              </select>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            {row.isDuplicate ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 rounded">
                                <AlertTriangle size={12} />
                                Doublon
                              </span>
                            ) : row.selectedRole ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded">
                                <CheckCircle size={12} />
                                Prêt
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded">
                                Rôle requis
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Error message */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-800">
                  <AlertCircle size={18} />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              {/* Summary and actions */}
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  <span className="font-medium text-green-600">{rowsReadyToImport}</span> membre(s) prêt(s) à importer
                  {campusSelectionRequired && (
                    <span className="ml-2 text-red-600 font-medium">
                      (Sélectionnez un campus ci-dessus)
                    </span>
                  )}
                  {!campusSelectionRequired && !allRolesAssigned && (
                    <span className="ml-2 text-yellow-600">
                      (Veuillez attribuer un rôle à tous les membres)
                    </span>
                  )}
                  {!campusSelectionRequired && allRolesAssigned && !allSalariesFilled && (
                    <span className="ml-2 text-yellow-600">
                      (Veuillez saisir le salaire pour tous les membres)
                    </span>
                  )}
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleStartOver}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Retour
                  </button>
                  <button
                    onClick={handleImport}
                    disabled={rowsReadyToImport === 0 || campusSelectionRequired}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <Upload size={18} />
                    Importer ({rowsReadyToImport})
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step: Importing */}
          {step === 'importing' && (
            <div className="py-12 text-center">
              <Loader2 size={48} className="mx-auto mb-4 text-blue-600 animate-spin" />
              <p className="text-lg font-medium text-gray-900 mb-2">
                Import en cours...
              </p>
              <p className="text-sm text-gray-500">
                Veuillez patienter, cela peut prendre quelques minutes
              </p>
            </div>
          )}

          {/* Step: Results */}
          {step === 'results' && results && (
            <div>
              {/* Success banner */}
              {results.successCount > 0 && (
                <div className="mb-6 p-4 bg-green-100 border border-green-300 rounded-lg">
                  <div className="flex items-center gap-3">
                    <CheckCircle size={24} className="text-green-600 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-green-800">
                        Import terminé avec succès !
                      </p>
                      <p className="text-sm text-green-700">
                        {results.successCount} membre{results.successCount > 1 ? 's' : ''} du personnel importé{results.successCount > 1 ? 's' : ''}.
                        Les emails d'activation ont été envoyés.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Summary */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                {/* Success */}
                <div className="p-4 bg-green-50 rounded-lg text-center">
                  <CheckCircle size={24} className="mx-auto mb-2 text-green-600" />
                  <p className="text-2xl font-bold text-green-700">{results.successCount}</p>
                  <p className="text-sm text-green-600">Importés</p>
                </div>

                {/* Skipped */}
                <div className="p-4 bg-yellow-50 rounded-lg text-center">
                  <SkipForward size={24} className="mx-auto mb-2 text-yellow-600" />
                  <p className="text-2xl font-bold text-yellow-700">{results.skippedCount}</p>
                  <p className="text-sm text-yellow-600">Ignorés (doublons)</p>
                </div>

                {/* Errors */}
                <div className="p-4 bg-red-50 rounded-lg text-center">
                  <XCircle size={24} className="mx-auto mb-2 text-red-600" />
                  <p className="text-2xl font-bold text-red-700">{results.errorCount}</p>
                  <p className="text-sm text-red-600">Erreurs</p>
                </div>
              </div>

              {/* Skipped duplicates list */}
              {results.skippedDuplicates && results.skippedDuplicates.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-medium text-yellow-800 mb-2 flex items-center gap-2">
                    <AlertTriangle size={16} />
                    Doublons ignorés ({results.skippedDuplicates.length})
                  </h4>
                  <div className="max-h-40 overflow-y-auto bg-yellow-50 rounded-lg p-3 text-sm">
                    {results.skippedDuplicates.map((dup, idx) => (
                      <div key={idx} className="flex justify-between py-1 border-b border-yellow-100 last:border-0">
                        <span className="text-yellow-900">
                          Ligne {dup.rowNumber}: {dup.prenom} {dup.nom}
                        </span>
                        <span className="text-yellow-700">{dup.reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Rejected list */}
              {results.rejectedMissingRequired && results.rejectedMissingRequired.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-medium text-red-800 mb-2 flex items-center gap-2">
                    <Ban size={16} />
                    Rejetés (champs requis manquants) ({results.rejectedMissingRequired.length})
                  </h4>
                  <div className="max-h-40 overflow-y-auto bg-red-50 rounded-lg p-3 text-sm">
                    {results.rejectedMissingRequired.map((rej, idx) => (
                      <div key={idx} className="flex justify-between py-1 border-b border-red-100 last:border-0">
                        <span className="text-red-900">
                          Ligne {rej.rowNumber}: {rej.prenom} {rej.nom}
                        </span>
                        <span className="text-red-700">{rej.reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Error list */}
              {results.results && results.results.filter(r => !r.success && r.error).length > 0 && (
                <div className="mb-4">
                  <h4 className="font-medium text-red-800 mb-2 flex items-center gap-2">
                    <AlertCircle size={16} />
                    Erreurs d'import
                  </h4>
                  <div className="max-h-40 overflow-y-auto bg-red-50 rounded-lg p-3 text-sm">
                    {results.results
                      .filter(r => !r.success && r.error)
                      .map((res, idx) => (
                        <div key={idx} className="flex justify-between py-1 border-b border-red-100 last:border-0">
                          <span className="text-red-900">
                            Ligne {res.rowNumber}: {res.prenom} {res.nom}
                          </span>
                          <span className="text-red-700">{res.error}</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleStartOver}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Importer un autre fichier
                </button>
                <button
                  onClick={handleClose}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Fermer
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== API FUNCTION ====================

interface BulkImportStaffData {
  rowNumber: number;
  cnie: string;
  passport: string;
  nom: string;
  prenom: string;
  email: string;
  sexe: string;
  dateNaissance: string;
  nationalite: string;
  fonction: string;
  dateRecrutement: string;
  salaireMensuel: string;
  handicap: string;
  role: string;
  isAlsoProfessor: boolean;
}

interface BulkImportStaffOptions {
  campusIds?: string[];
  academicYearId?: string;
}

async function bulkImportStaff(
  data: BulkImportStaffData[],
  options: BulkImportStaffOptions = {}
): Promise<BulkImportStaffResponse> {
  // Import axios with interceptors configured (handles 401 automatically)
  const axios = (await import('@/config/axios.config')).default;

  // Build request body - only include staff array, optional fields added conditionally
  const requestBody: Record<string, unknown> = {
    staff: data,
  };

  // Only add campusIds if there are actual campus IDs selected
  // This prevents sending empty array which causes validation error
  if (options.campusIds && options.campusIds.length > 0) {
    requestBody.campusIds = options.campusIds;
  }

  // Only add academicYearId if provided
  if (options.academicYearId) {
    requestBody.academicYearId = options.academicYearId;
  }

  try {
    const response = await axios.post<BulkImportStaffResponse>(
      '/api/users/staff/bulk-import',
      requestBody
    );
    return response.data;
  } catch (error: unknown) {
    // Extract error message from axios error
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as { response?: { data?: { message?: string } } };
      throw new Error(axiosError.response?.data?.message || 'Erreur lors de l\'import du personnel');
    }
    throw new Error('Erreur lors de l\'import du personnel');
  }
}

export default BulkImportStaffModal;
